import type { UpdateBookingRequest } from "api-client";
import type { EditBookingLoadedFees } from "./booking-detail.mapper";
import {
  buildCreateBookingRequest,
  type CreateBookingWizardDraft,
} from "./create-booking-payload.mapper";

export interface EditBookingWizardDraft extends CreateBookingWizardDraft {
  bookingId: number;
  loadedFees?: EditBookingLoadedFees | null;
}

type UpdateBookingScheduleFields = Partial<
  Pick<
    UpdateBookingRequest,
    "scheduleDate" | "scheduleTime" | "mustArriveByDate" | "mustArriveByTime"
  >
>;

/** PUT .../bookings/{bookingId} — single branch per bookingType (FR-AV44-020 / AV-132). */
export function buildUpdateBookingRequest(draft: EditBookingWizardDraft): UpdateBookingRequest {
  const createBody = buildCreateBookingRequest(draft);
  const scheduleFields = resolveUpdateScheduleFields(createBody);
  const fees = resolveUpdateBookingFees(draft);

  const base: UpdateBookingRequest = {
    bookingId: draft.bookingId,
    customerId: createBody.customerId,
    locationId: createBody.locationId,
    bookingType: createBody.bookingType,
    ...scheduleFields,
    ...(createBody.dayRate !== undefined
      ? { dayRate: createBody.dayRate }
      : {}),
    baseSurchargeFee: fees.baseSurchargeFee,
    afterHour: fees.afterHour,
    weekendMultiplier: fees.weekendMultiplier,
    totalCostEstimate: fees.totalCostEstimate,
    sealCapture: createBody.sealCapture,
  };

  if (createBody.equipmentPieces) {
    base.equipmentPieces = createBody.equipmentPieces;
  }

  if (createBody.bookingType === "pickup") {
    return {
      ...base,
      pickup: createBody.pickup,
    };
  }

  return {
    ...base,
    dropoff: createBody.dropoff,
  };
}

function resolveUpdateScheduleFields(
  createBody: ReturnType<typeof buildCreateBookingRequest>,
): UpdateBookingScheduleFields {
  const fields: UpdateBookingScheduleFields = {};

  if (createBody.scheduleDate) {
    fields.scheduleDate = createBody.scheduleDate;
    if (createBody.scheduleTime) {
      fields.scheduleTime = createBody.scheduleTime;
    }
  }

  if (createBody.mustArriveByDate) {
    fields.mustArriveByDate = createBody.mustArriveByDate;
    if (createBody.mustArriveByTime) {
      fields.mustArriveByTime = createBody.mustArriveByTime;
    }
  }

  return fields;
}

function resolveUpdateBookingFees(draft: EditBookingWizardDraft): EditBookingLoadedFees {
  if (draft.loadedFees) {
    return draft.loadedFees;
  }
  const dayRate = draft.costPayload?.dayRate ?? 0;
  return {
    baseSurchargeFee: dayRate ?? 0,
    afterHour: 0,
    weekendMultiplier: 0,
    totalCostEstimate: dayRate ?? 0,
  };
}
